import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { User as UserIcon, Lock, Save, Loader2 } from "lucide-react";

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/auth/profile", { name, email });
      await refresh();
      toast.success("Profil güncellendi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Güncellenemedi");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error("Yeni şifre en az 6 karakter olmalı"); return; }
    if (newPassword !== confirmPassword) { toast.error("Şifreler eşleşmiyor"); return; }
    setSavingPassword(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Şifre değiştirildi");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Değiştirilemedi");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Hesap</div>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">Profil Ayarları</h1>
        <p className="text-sm text-muted-foreground mt-1">Ad, e-posta ve şifreni güncelle. Değişiklikler anında geçerli olur.</p>
      </div>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
            <UserIcon className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display font-semibold">Kişisel Bilgiler</div>
            <div className="text-xs text-muted-foreground">Ad ve e-posta adresi</div>
          </div>
        </div>
        <form onSubmit={saveProfile} className="space-y-4" data-testid="profile-form">
          <div className="space-y-2">
            <Label>Ad Soyad</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="profile-name" />
          </div>
          <div className="space-y-2">
            <Label>E-posta</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="profile-email" />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={savingProfile} data-testid="save-profile-btn" className="rounded-full">
              {savingProfile ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Kaydediliyor</> : <><Save className="h-4 w-4 mr-2" /> Değişiklikleri Kaydet</>}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
            <Lock className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display font-semibold">Şifre Değiştir</div>
            <div className="text-xs text-muted-foreground">En az 6 karakter</div>
          </div>
        </div>
        <form onSubmit={savePassword} className="space-y-4" data-testid="password-form">
          <div className="space-y-2">
            <Label>Mevcut Şifre</Label>
            <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required data-testid="password-current" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Yeni Şifre</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required data-testid="password-new" />
            </div>
            <div className="space-y-2">
              <Label>Yeni Şifre (Tekrar)</Label>
              <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="password-confirm" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={savingPassword} data-testid="save-password-btn" className="rounded-full">
              {savingPassword ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Değiştiriliyor</> : <><Save className="h-4 w-4 mr-2" /> Şifreyi Değiştir</>}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
